/**
 * @file edit-order-details.tsx
 * @module app/modules/orders-delivery/edit-order-details
 * @description Orders delivery module component for UTrade Admin.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { Button, Card, Col, Form, InputGroup, Modal, Row } from 'react-bootstrap';
import CrossIcon from '../../../../../_admin/assets/media/svg_uTrade/cross_gray.svg';
import editIcon from '../../../../../_admin/assets/media/svg_uTrade/edit-round-dark.svg';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ordersDelivery } from '../../../../../api/apiEndPoints';
import APICallService from '../../../../../api/apiCallService';
import Loader from '../../../../../Global/loader';
import {
  CartonWithDozens,
  CartonWithPieces,
  Dozen,
  OrderCard,
  OrderCash,
  OrderMPesa,
  OrdersDelivery,
  OrderTigoPesa,
  Piece,
  OrderCancelled,
  Manager,
  ServiceMember,
  RouteOrderLoaded,
  AddressEditPermission,
  OrderCancelPermission,
  UtradeDiscountPermission,
  EditBypassPermission,
  Admin,
} from '../../../../../utils/constants';
import Method from '../../../../../utils/methods';
import clsx from 'clsx';
import { error, success } from '../../../../../Global/toast';
import { orderDeliveryJSON } from '../../../../../api/apiJSON/order';
import { useOrderQueuePolling } from '../../hooks/use-order-queue-polling';
import Validations from '../../../../../utils/validations';
import { useAuth } from '../../../auth';
import { useWarehouse } from '../../../../context/WarehouseContext';
import ImageWithFallback from '../../../_common/components/image-with-fallback';
import OrderInfoRow from '../../components/order-details/order-info-row';
import '../../styles/order-details.css';

// Modals load on first use. The address modal is preloaded when the page opens in address mode,
// because it opens straight away there.
const loadChangeDeliveryAddress = () =>
  import('../../../../modals/change-delivery-address');
const ChangeDeliveryAddress = lazy(loadChangeDeliveryAddress);
const ConfirmChanges = lazy(() => import('../../../../modals/confirm-changes'));

const hasExplicitOrdersDeliveryPermission = (
  currentUser: any,
  permission: number
): boolean => {
  const permissions = currentUser?.roleAndPermission?.permissions;
  if (!permissions?.length) return false;

  const ordersDeliveryEntry = permissions.find(
    (item: any) => Number(item.module) === Number(OrdersDelivery)
  );
  if (!ordersDeliveryEntry?.permissions?.length) return false;

  return ordersDeliveryEntry.permissions
    .map((value: any) => Number(value))
    .includes(Number(permission));
};

const EditOrderDetails = () => {
  const { selectedWarehouse } = useWarehouse();
  const { currentUser } = useAuth();
  const hasEditBypass = hasExplicitOrdersDeliveryPermission(
    currentUser,
    EditBypassPermission
  );
  // Edit product & quantities requires explicit Edit Bypass, except Super Admin.
  const canEditProducts =
    currentUser?.userType === Admin || hasEditBypass;
  const canEditAddress = Method.hasPermission(
    OrdersDelivery,
    AddressEditPermission,
    currentUser
  );
  const canCancelOrder = Method.hasPermission(
    OrdersDelivery,
    OrderCancelPermission,
    currentUser
  );
  const canEditUtradeDiscount = Method.hasPermission(
    OrdersDelivery,
    UtradeDiscountPermission,
    currentUser
  );
  const navigate = useNavigate();
  const { state }: any = useLocation();
  const isAddressOnlyMode = Boolean(state?.addressOnly);
  const allowProductEdit = canEditProducts && !isAddressOnlyMode;
  const allowAddressEdit = canEditAddress && isAddressOnlyMode;
  const allowDiscountEdit = canEditUtradeDiscount && !isAddressOnlyMode;
  const [details, setDetails] = useState<any>();
  const [discountValue, setDiscountValue] = useState<any>([]);
  const [oldDiscount, setOldDiscount] = useState<any>([]);
  const [discount, setDiscount] = useState<boolean>(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [initData, setInitData] = useState<any>();
  const [selectedProduct, setSelectedProduct] = useState<any>([]);
  const [selectedProductValidation, setSelectedProductValidation] =
    useState<any>([]);
  const [discountValidation, setDiscountValidation] = useState<boolean>(false);
  const [changeDeliveryAddress, setChangeDeliveryAddress] = useState(false);
  const [orderValue, setOrderValue] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState<any>();
  const [isOrderPlaced, setIsOrderPlaced] = useState<any>(false);
  const [confirmModalForCancelOrder, setConfirmModalForCancelOrder] =
    useState(false);
  const [showSaveChangesModal, setShowSaveChangesModal] =
    useState(false);
  const [modificationReason, setModificationReason] = useState('');
  const [modificationReasonError, setModificationReasonError] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);

  const [validation, setValidation] = useState<{
    [key: string]: {
      isInvalid: boolean;
      remainingQty: number;
      maxLimit?: number;
      limitType?: 'inventory' | 'order' | 'both' | 'unlimited';
    };
  }>({});

  const isProductLoaded = (product: any) =>
    product?.isLoaded || product?.status === RouteOrderLoaded;

  const hasLoadedProductQuantityChanged = (
    initialProducts: any[] = [],
    updatedProducts: any[] = []
  ) =>
    updatedProducts.some((product, index) => {
      if (!isProductLoaded(product)) {
        return false;
      }
      return (
        Number(product?.stockCount) !==
        Number(initialProducts[index]?.stockCount)
      );
    });

  const hasQuantityChanges = (
    initialProducts: any[] = [],
    updatedProducts: any[] = []
  ) =>
    updatedProducts.some(
      (product, index) =>
        Number(product?.stockCount) !==
        Number(initialProducts[index]?.stockCount)
    );

  const hasUnsavedChanges = () => {
    const hasDiscountChange =
      Number.parseInt(discountValue?.[0]?.value || 0) !==
      Number.parseInt(oldDiscount?.[0]?.value || 0);
    const hasAddressChange =
      initData?.record?.address?.reference !==
      details?.record?.address?.reference;

    return (
      hasQuantityChanges(
        initData?.record?.variants || [],
        selectedProduct
      ) ||
      hasDiscountChange ||
      hasAddressChange
    );
  };

  const getOrderLimit = (product: any, type: any): number => {
    const orderLimits = product?.variant?.orderLimit || [];
    if (!orderLimits.length) {
      return Infinity;
    }

    const warehouseId = state?.warehouseId ?? selectedWarehouse?.value;
    const normalizedType = Number(type);

    const limitObj = orderLimits.find((limit: any) => {
      return (
        Number(limit.qtyType) === normalizedType &&
        warehouseId &&
        String(limit.warehouseId) === String(warehouseId)
      );
    });

    if (limitObj) {
      return Number(limitObj?.limit ?? Infinity);
    }

    const fallbackLimitObj = orderLimits.find(
      (limit: any) =>
        Number(limit.qtyType) === normalizedType && limit.defaultWarehouse
    );

    if (fallbackLimitObj) {
      return Number(fallbackLimitObj?.limit ?? Infinity);
    }

    const typeOnlyFallback = orderLimits.find(
      (limit: any) => Number(limit.qtyType) === normalizedType
    );

    return Number(typeOnlyFallback?.limit ?? Infinity);
  };

  const getEffectiveMaxLimit = (
    product: any,
    type: any,
    inventoryRemaining: number
  ): {
    maxLimit: number;
    limitType: 'inventory' | 'order' | 'both' | 'unlimited';
  } => {
    const orderLimit = getOrderLimit(product, type);

    if (orderLimit === -1) {
      return { maxLimit: inventoryRemaining, limitType: 'unlimited' };
    }

    if (orderLimit === Infinity) {
      return { maxLimit: inventoryRemaining, limitType: 'inventory' };
    }

    if (orderLimit < inventoryRemaining) {
      return { maxLimit: orderLimit, limitType: 'order' };
    }

    if (orderLimit > inventoryRemaining) {
      return { maxLimit: inventoryRemaining, limitType: 'inventory' };
    }

    return { maxLimit: inventoryRemaining, limitType: 'both' };
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    void (async () => {
      if (isAddressOnlyMode ? !canEditAddress : !canEditProducts) {
        return window.history.back();
      }
      if (isAddressOnlyMode) {
        void loadChangeDeliveryAddress();
      }
      await fetchOrderData(state?.id);
      if (isAddressOnlyMode && canEditAddress) {
        setChangeDeliveryAddress(true);
      }
    })();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */
  const fetchOrderData = async (id: string) => {
    setFetchLoading(true);
    const params: any = { record: id };
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    let apiService = new APICallService(
      ordersDelivery.init,
      params,
      '',
      '',
      '',
      '',
      OrdersDelivery,
      selectedWarehouse?.value
    );
    let response = await apiService.callAPI();
    if (response) {
      if (response?.record?.variants) {
        response.record.variants = response.record.variants
          .filter((val: any) => Number(val.stockCount) > 0)
          .map((val: any) => ({
            ...val,
            isLoaded: isProductLoaded(val),
          }));
      }
      const validationArray = Array.from(
        { length: response?.record?.variants?.length },
        () => ({
          stockCount: false,
        })
      );
      setSelectedProductValidation(validationArray);
      if (
        response?.record?.payment?.discounts?.length
      ) {
        setDiscount(true);
      }
      setDiscountValue(response?.record?.payment?.discounts);
      setOldDiscount(
        JSON.parse(JSON.stringify(response?.record?.payment?.discounts))
      );
      setSelectedProduct(response?.record?.variants);
      setDetails(JSON.parse(JSON.stringify({ ...response })));
      setInitData(JSON.parse(JSON.stringify({ ...response })));
    }
    setFetchLoading(false);
  };

  const calculateRemainingQty = (
    warehouseInventory: any,
    type: any
  ): number => {
    return (
      warehouseInventory?.quantityTypes?.find(
        (sval: any) => sval?.type === type
      )?.remainingQty ?? 0
    );
  };

  const handleQuantityChange = async (
    value: any,
    index: number,
    product: any,
    type: any
  ) => {
    if (!allowProductEdit) {
      return;
    }
    if (isProductLoaded(selectedProduct[index])) {
      return;
    }
    value = parseInt(Method.trimLeadingZeros(value));
    product = JSON.parse(JSON.stringify(product));
    const validationKey = `${type}-${product?.variant?._id || ''}`; // assuming product has option key

    // Get the selected warehouse ID
    const warehouseId = state?.warehouseId ?? selectedWarehouse?.value;

    // Find inventory for the selected warehouse
    const warehouseInventory = product?.variant?.inventoryInfo?.find(
      (inv: any) => inv?.warehouseId === warehouseId
    );

    const item = warehouseInventory?.quantityTypes?.find(
      (qt: any) => qt.type === type
    );

    let stockCount = initData?.record?.variants[index].stockCount;
    const {
      dozensInCarton = 0,
      piecesInCarton = 0,
      piecesInDozen = 0,
    } = product.variant;

    let remainingQty = 0;

    // --- Validation logic ---
    if (type === Dozen) {
      const dozenQty = calculateRemainingQty(warehouseInventory, Dozen);
      const cartonWithDozenQty = calculateRemainingQty(
        warehouseInventory,
        CartonWithDozens
      );
      remainingQty =
        stockCount + dozenQty + cartonWithDozenQty * dozensInCarton;
    } else if (type === Piece) {
      const pieceQty = calculateRemainingQty(warehouseInventory, Piece);
      let dozenQty =
        calculateRemainingQty(warehouseInventory, CartonWithDozens) *
        dozensInCarton;
      let dozenIndex = warehouseInventory?.quantityTypes.findIndex(
        (sval: any) => sval.type === Dozen
      );
      if (dozenIndex !== -1 && warehouseInventory) {
        warehouseInventory.quantityTypes[dozenIndex].remainingQty =
          warehouseInventory.quantityTypes[dozenIndex].remainingQty + dozenQty;
        dozenQty =
          calculateRemainingQty(warehouseInventory, Dozen) * piecesInDozen;
      }
      const cartonQty =
        calculateRemainingQty(warehouseInventory, CartonWithPieces) *
        piecesInCarton;

      remainingQty = stockCount + pieceQty + dozenQty + cartonQty;
    } else {
      if (item) {
        remainingQty = stockCount + item?.remainingQty || 0;
      } else {
        remainingQty = stockCount;
      }
    }

    const { maxLimit, limitType } = getEffectiveMaxLimit(
      product,
      type,
      remainingQty
    );

    let validatedValue = value;
    if (value > maxLimit || !maxLimit) {
      validatedValue = maxLimit;
    }

    // --- Update product ---
    const temp = { ...product };
    temp.stockCount = validatedValue || 1;
    // --- Update details ---
    const tempData = [...selectedProduct];
    const tempValidation = [...selectedProductValidation];
    tempData[index].stockCount = validatedValue;
    tempValidation[index].stockCount = false;
    if (!value && value !== 0) {
      tempValidation[index].stockCount = true;
    }
    setSelectedProductValidation(tempValidation);
    setSelectedProduct(tempData);

    // --- Set validation ---
    const newValidation: any = {
      [validationKey]: {
        isInvalid: value > maxLimit,
        remainingQty: maxLimit,
        maxLimit: maxLimit,
        limitType: limitType,
      },
    };
    setValidation(newValidation);
  };
  const handleSaveAddress = (address: any) => {
    let temp = { ...details };
    address.reference = address?._id;
    temp.record.address = address;
    setDetails(temp);
    setChangeDeliveryAddress(false);
  };
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    let total = calculateTotalAmount(selectedProduct);
    let temp = {
      totalAmount: total.totalAmount,
      totalAmountWithTax: total.totalAmountTax,
      commission: total.commission,
      discount: total.discount,
      subTotal: total.subTotal,
    };
    setOrderValue(temp);
  }, [selectedProduct]);
  /* eslint-enable react-hooks/exhaustive-deps */
  const calculateTotalAmount = (variantsData: any) => {
    let totalAmount = 0;
    let totalAmountTax = 0;
    let totalCommission = 0;
    const isPharmacyOrder =
      details?.record?.isPharmacyOrder === true ||
      details?.record?.isPharmacyOrder === 'true' ||
      details?.record?.postData?.isPharmacyOrder === true ||
      details?.record?.postData?.isPharmacyOrder === 'true';

    variantsData.forEach((variantVal: any) => {
      if (isPharmacyOrder) {
        const stockCount = Number(variantVal?.stockCount || 0);
        const amount = Number(variantVal?.amount || 0);
        const beforeTaxAmount = Number(
          variantVal?.beforeTaxAmount ?? variantVal?.amount ?? 0
        );
        totalAmount += stockCount * amount;
        totalAmountTax += stockCount * beforeTaxAmount;
        totalCommission +=
          (stockCount * amount * Number(variantVal?.commission || 0)) / 100;
        return;
      }

      if (!variantVal.taxFree && variantVal.productTax) {
        const stockCount = variantVal.stockCount;
        let discountedPrice =
          stockCount *
          getVariantUnitAndTotalPriceByQuantityType(
            variantVal.clonedQuantityType,
            variantVal.stockCount
          ).unitPrice;
        let discountedTaxPrice =
          stockCount *
          (getVariantUnitAndTotalPriceByQuantityType(
            variantVal.clonedQuantityType,
            variantVal.stockCount
          ).unitPrice /
            (1 + variantVal.productTax / 100));
        totalAmount += discountedPrice;
        totalAmountTax += discountedTaxPrice;
        totalCommission += (discountedPrice * variantVal.commission) / 100;
      } else {
        const stockCount = variantVal.stockCount;
        let discountedPrice =
          stockCount *
          getVariantUnitAndTotalPriceByQuantityType(
            variantVal.clonedQuantityType,
            variantVal.stockCount
          ).unitPrice;
        let discountedTaxPrice =
          stockCount *
          getVariantUnitAndTotalPriceByQuantityType(
            variantVal.clonedQuantityType,
            variantVal.stockCount
          ).unitPrice;
        totalAmount += discountedPrice;
        totalAmountTax += discountedTaxPrice;
        totalCommission += (discountedPrice * variantVal.commission) / 100;
      }
    });
    return {
      totalAmount: totalAmount,
      totalAmountTax: totalAmountTax,
      commission: totalCommission,
      subTotal: variantsData.reduce((p: any, c: any) => {
        let actualCharge = c.stockCount * c.originalAmount;
        return p + actualCharge;
      }, 0),
      discount: variantsData.reduce((p: any, c: any) => {
        let actualCharge = c.stockCount * c.originalAmount;
        let discountedCharge = c.stockCount * c.amount;
        return p + actualCharge - discountedCharge;
      }, 0),
    };
  };
  const isPharmacyOrder =
    details?.record?.isPharmacyOrder === true ||
    details?.record?.isPharmacyOrder === 'true' ||
    details?.record?.postData?.isPharmacyOrder === true ||
    details?.record?.postData?.isPharmacyOrder === 'true';

  const getEditOrderUnitPrice = (variantVal: any) => {
    if (isPharmacyOrder) {
      return Number(variantVal?.amount || 0);
    }
    return Number(
      getVariantUnitAndTotalPriceByQuantityType(
        variantVal?.clonedQuantityType,
        variantVal?.stockCount
      )?.unitPrice || 0
    );
  };

  const getEditOrderOriginalUnitPrice = (variantVal: any) => {
    if (isPharmacyOrder) {
      return Number(variantVal?.originalAmount ?? variantVal?.amount ?? 0);
    }
    return Number(
      getVariantUnitAndTotalPriceByQuantityType(
        variantVal?.clonedQuantityType,
        variantVal?.stockCount
      )?.originalPrice || 0
    );
  };

  const getEditOrderTotalPrice = (variantVal: any) => {
    return (
      Number(variantVal?.stockCount || 0) * getEditOrderUnitPrice(variantVal)
    );
  };

  const getEditOrderOriginalTotalPrice = (variantVal: any) => {
    return (
      Number(variantVal?.stockCount || 0) *
      getEditOrderOriginalUnitPrice(variantVal)
    );
  };

  const handleOnKeyPress = (event: any) => {
    var charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
      event.preventDefault();
      return false;
    }
    return true;
  };
  const generateProductLogs = (
    initData: any,
    selectedProduct: any,
    customerName: string,
    discount: any,
    oldDiscount: any
  ) => {
    const logs: any = [];
    selectedProduct.forEach((product: any, index: number) => {
      const initialProduct = initData[index];
      if (initialProduct?.stockCount !== product?.stockCount) {
        logs.push({
          path: `Product "${product.variant?.title}" stockCount`,
          oldValue: initialProduct?.stockCount,
          newValue: product?.stockCount,
        });
      }
      if (initialProduct?.totalAmount !== product?.totalAmount) {
        logs.push({
          path: `Product "${product.variant?.title}" totalAmount`,
          oldValue: initialProduct?.totalAmount,
          newValue: product?.totalAmount,
        });
      }
    });

    if (parseInt(discount[0]?.value || 0) !== parseInt(oldDiscount[0]?.value)) {
      logs.push({
        path: `UTrade Discount`,
        oldValue: oldDiscount[0]?.value,
        newValue: discount[0]?.value,
      });
    }
    return { logs, customerName };
  };

  const handleCancelOrder = async () => {
    if (!canCancelOrder) {
      error('You do not have permission to cancel this order');
      return;
    }
    setCancelLoading(true);
    let params: any = { status: OrderCancelled };
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    let apiService = new APICallService(
      ordersDelivery.status,
      params,
      { id: details.record._id },
      '',
      '',
      '',
      OrdersDelivery,
      selectedWarehouse?.value || null
    );
    let response = await apiService.callAPI();
    if (response) {
      success('Order cancelled successfully');
      if (currentUser?.role.some((obj: any) => obj.type === Manager)) {
        navigate('/sales-management/manager/orders');
      } else if (
        currentUser?.role.some((obj: any) => obj.type === ServiceMember)
      ) {
        navigate('/sales-management/representative/orders');
      } else {
        navigate('/orders');
      }
    } else {
      error(response.error);
    }
    setCancelLoading(false);
    setConfirmModalForCancelOrder(false);
  };

  const handleEditOrder = async (reasonForModification: string = '') => {
    if (
      hasLoadedProductQuantityChanged(
        initData?.record?.variants || [],
        selectedProduct
      )
    ) {
      error('Loaded products cannot be edited');
      return;
    }

    let validateTemp = [...selectedProductValidation];
    const allTrue = validateTemp?.every(
      (obj: any) => obj?.stockCount === false
    );
    let addressChange = '';

    if (
      initData?.record?.address?.reference !==
      details?.record?.address?.reference
    ) {
      addressChange = details?.record?.address?.addressLine1;
    }
    if (!allTrue) {
    } else {
      if (selectedProduct?.every((val: any) => val.stockCount === 0)) {
        if (!canCancelOrder) {
          error('You do not have permission to cancel this order');
          return;
        }
        setConfirmModalForCancelOrder(true);
      } else if (discountValue?.length) {
        if (
          parseInt(discountValue[0]?.value) > parseInt(orderValue?.totalAmount)
        ) {
          error('Discount amount should be less than original total');
        } else {
          if (checkStockCounts(initData?.record.variants, selectedProduct)) {
            setLoading(true);
            if (
              deepCompare(initData?.record?.variants, selectedProduct).length >
                0 ||
              discountValue[0]?.value
            ) {
              let apiService = new APICallService(
                ordersDelivery.edit,
                orderDeliveryJSON.editOrder({
                  buyer: details.record.customer.reference,
                  variants: selectedProduct.filter(
                    (val: any) => val.stockCount > 0
                  ),
                  address: details.record.address.reference,
                  discounts: discountValue,
                  record: details.record._id,
                  reason: reasonForModification,
                }),
                '',
                '',
                '',
                '',
                OrdersDelivery,
                selectedWarehouse?.value || null
              );
              let response = await apiService.callAPI();
              if (response) {
                setOrderId(response.queueId);
                // navigate('/orders');
                setIsOrderPlaced(true);
                const customerName = details.record.customer.name;
                const data = generateProductLogs(
                  initData.record.variants,
                  selectedProduct,
                  customerName,
                  discountValue,
                  oldDiscount
                );

                
              } else {
                setLoading(false);
              }
            } else {
              // navigate('/orders');
            }
          }
        }
      } else {
        if (checkStockCounts(initData?.record?.variants, selectedProduct)) {
          setLoading(true);
          if (selectedProduct?.every((val: any) => val.stockCount === 0)) {
            if (!canCancelOrder) {
              error('You do not have permission to cancel this order');
              setLoading(false);
              return;
            }
            setConfirmModalForCancelOrder(true);
            setLoading(false);
          } else if (
            deepCompare(initData?.record?.variants, selectedProduct)?.length >
              0 ||
            discountValue[0]?.value
          ) {
            let apiService = new APICallService(
              ordersDelivery.edit,
              orderDeliveryJSON.editOrder({
                buyer: details?.record?.customer?.reference,
                variants: selectedProduct.filter(
                  (val: any) => val.stockCount > 0
                ),
                address: details?.record?.address?.reference,
                discounts: discountValue,
                record: details?.record?._id,
                reason: reasonForModification,
              }),
              '',
              '',
              '',
              '',
              OrdersDelivery,
              selectedWarehouse?.value || null
            );
            let response = await apiService.callAPI();
            if (response) {
              setOrderId(response?.queueId);
              setIsOrderPlaced(true);
              const customerName = details?.record?.customer?.name;
              const data = generateProductLogs(
                initData.record.variants,
                selectedProduct,
                customerName,
                discountValue,
                oldDiscount
              );
              
            } else {
              setLoading(false);
            }
          } else {
            if (
              initData?.record?.address?.reference !==
              details?.record?.address?.reference
            ) {
              let apiService = new APICallService(
                ordersDelivery.edit,
                orderDeliveryJSON.editOrder({
                  buyer: details?.record?.customer?.reference,
                  variants: selectedProduct.filter(
                    (val: any) => val.stockCount > 0
                  ),
                  address: details?.record?.address?.reference,
                  discounts: discountValue,
                  record: details?.record?._id,
                  reason: reasonForModification,
                }),
                '',
                '',
                '',
                '',
                OrdersDelivery
              );
              let response = await apiService.callAPI();
              if (response) {
                setOrderId(response.queueId);
                // navigate('/orders');
                setIsOrderPlaced(true);
                const customerName = details?.record?.customer?.name;
                const data = generateProductLogs(
                  initData.record.variants,
                  selectedProduct,
                  customerName,
                  discountValue,
                  oldDiscount
                );

                
              } else {
                setLoading(false);
              }
            } else {
              navigate('/orders');
            }
          }
        }
      }
    }
  };
  function deepCompare(arr1: any, arr2: any) {
    const changes = [];
    // Check if the arrays have the same length
    if (arr1.length !== arr2.length) {
      throw new Error('Arrays must have the same length.');
    }
    // Iterate through each object in the arrays
    for (let i = 0; i < arr1.length; i++) {
      const obj1 = arr1[i];
      const obj2 = arr2[i];
      // Compare stockCount property
      if (obj1.stockCount !== obj2.stockCount) {
        changes.push({
          index: i,
          property: 'stockCount',
          oldValue: obj1.stockCount,
          newValue: obj2.stockCount,
        });
      }
      // Compare amount property (which might be affected by discount)
      if (obj1.amount !== obj2.amount) {
        changes.push({
          index: i,
          property: 'amount',
          oldValue: obj1.amount,
          newValue: obj2.amount,
        });
      }
      // Compare totalAmount property (which might be affected by discount)
      if (obj1.totalAmount !== obj2.totalAmount) {
        changes.push({
          index: i,
          property: 'totalAmount',
          oldValue: obj1.totalAmount,
          newValue: obj2.totalAmount,
        });
      }
    }
    return changes;
  }
  function checkStockCounts(a: any, b: any) {
    const stockCountsA: any = {};
    // Create a map of variant._id to stockCount in array a
    for (const item of a) {
      stockCountsA[item.variant._id + '_' + item.quantityType] =
        item.stockCount;
    }
    // Iterate through array b and check stockCounts
    for (const item of b) {
      const variantId = item.variant._id;
      const stockCountB = item.stockCount;
      if (
        stockCountsA.hasOwnProperty(variantId) &&
        stockCountsA[variantId] <= stockCountB
      ) {
        error(`Updated quantity must be less than original quantity`);
        return false;
      }
    }
    return true;
  }
  const closeSaveChangesModal = () => {
    if (loading) {
      return;
    }
    setShowSaveChangesModal(false);
    setModificationReason('');
    setModificationReasonError('');
  };

  const handleSaveWithReason = async () => {
    const trimmedReason = modificationReason.trim();
    if (!trimmedReason) {
      setModificationReasonError('Please enter reason for modification.');
      return;
    }
    setShowSaveChangesModal(false);
    await handleEditOrder(trimmedReason);
  };

  const handleDiscount = async (value: string) => {
    if (!allowDiscountEdit) {
      return;
    }
    let priceValidation = await Validations.validatePrice(value.trim());
    if (priceValidation) {
      let temp: any = [];
      if (parseInt(value)) {
        if (parseInt(value) > parseInt(orderValue.totalAmount)) {
          setDiscountValidation(true);
        } else {
          setDiscountValidation(false);
        }
        temp = [{ type: 1, value: value }];
      } else {
        temp = [];
      }
      setDiscountValue(temp);
    }
  };
  useOrderQueuePolling({
    isPolling: isOrderPlaced,
    orderId,
    warehouseId: selectedWarehouse?.value,
    onFailed: () => {
      setIsOrderPlaced(false);
      setLoading(false);
    },
    onProcessed: () => {
      setIsOrderPlaced(false);
      success('Order edited successfully');
      if (currentUser?.role.some((obj: any) => obj.type === Manager)) {
        navigate('/sales-management/manager/orders');
      } else if (
        currentUser?.role.some((obj: any) => obj.type === ServiceMember)
      ) {
        navigate('/sales-management/representative/orders');
      } else {
        navigate('/orders');
      }
      setLoading(false);
    },
  });
  function getVariantUnitAndTotalPriceByQuantityType(
    quantityTypeObj: any,
    quantity: any = 0
  ) {
    let unitPrice = quantityTypeObj['amount'] || 0;
    let originalPrice = quantityTypeObj['amount'] || 0;
    if (quantityTypeObj['discountAmtEnabled']) {
      unitPrice = quantityTypeObj['discountAmt'] || 0;
    } else if (quantityTypeObj['discountByQuantitiesEnabled']) {
      let availableDiscountRanges =
        quantityTypeObj['discountsByQuantities'] || [];
      if (availableDiscountRanges?.length > 0) {
        availableDiscountRanges?.sort((a: any, b: any) => a['min'] - b['min']);
        if (quantity < availableDiscountRanges[0]['min']) {
          //consider original price as it is
        } else {
          let { matchedRange, maxRange } = availableDiscountRanges?.reduce(
            (p: any, c: any) => {
              if (quantity >= c['min'] && quantity <= c['max']) {
                p.matchedRange = c;
              }
              if (quantity > c['min']) {
                p.maxRange = c;
              }
              return p;
            },
            {
              matchedRange: undefined,
              maxRange: undefined,
            }
          );
          if (matchedRange) {
            unitPrice = matchedRange['discountAmt'] || 0;
          } else {
            unitPrice = maxRange['discountAmt'] || 0;
          }
        }
      } else {
        //consider original price as it is
      }
    }
    return {
      originalPrice,
      unitPrice,
      totalPrice: quantity * unitPrice,
    };
  }
  return (
    <div className="p-3 p-md-5 od-page">
        {confirmModalForCancelOrder ? (
          <Suspense fallback={null}>
          <ConfirmChanges
            show={confirmModalForCancelOrder}
            title={'Are you sure you want to remove this item?'}
            save={'Yes'}
            message={''}
            onHide={() => {
              setConfirmModalForCancelOrder(false);
            }}
            removeMsg={
              'This is the last item in the order, removing this item will cancel the order'
            }
            isRemove={true}
            onSave={() => {
              void handleCancelOrder();
            }}
            loading={cancelLoading}
          />
          </Suspense>
        ) : (
          <></>
        )}
        <Modal
          show={showSaveChangesModal}
          onHide={closeSaveChangesModal}
          dialogClassName="modal-dialog-centered"
        >
          <Modal.Header closeButton>
            <Modal.Title className="fs-22 fw-bolder">Save Changes</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="fs-15 text-dark mb-4">
              Please provide reason for modification.
            </p>
            <Form.Group>
              <Form.Control
                as="textarea"
                rows={4}
                value={modificationReason}
                placeholder="Type reason for modification"
                onChange={(event: any) => {
                  setModificationReason(event.target.value);
                  if (modificationReasonError) {
                    setModificationReasonError('');
                  }
                }}
              />
            </Form.Group>
            {modificationReasonError ? (
              <div className="text-danger fs-13 mt-2">
                {modificationReasonError}
              </div>
            ) : (
              <></>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button className="uw-btn uw-btn-light"
              variant="light"
              onClick={closeSaveChangesModal}
              disabled={loading}
            >
              Close
            </Button>
            <Button className="uw-btn uw-btn-primary"
              variant="primary"
              onClick={handleSaveWithReason}
              disabled={loading}
            >
              {loading ? (
                <span
                  className="indicator-progress"
                  style={{ display: 'block' }}
                >
                  Please wait...
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              ) : (
                'Confirm Save'
              )}
            </Button>
          </Modal.Footer>
        </Modal>
        {/* Modals */}
        <Row className="g-5 g-md-8">
          <Col md={12}>
            <div className="od-page-header">
              <div className="od-page-header__main">
                <span className="od-page-header__eyebrow">Edit order</span>
                <h1 className="od-page-header__title">
                  {details?.record?.refKey || 'Edit order details'}
                  <span className="od-chip od-chip--info">
                    {isAddressOnlyMode
                      ? 'Delivery address'
                      : 'Products & quantities'}
                  </span>
                </h1>
                {details?.record?.customer?.name ? (
                  <div className="od-page-header__meta">
                    <span>
                      Customer <strong>{details.record.customer.name}</strong>
                    </span>
                  </div>
                ) : (
                  <></>
                )}
              </div>
              <Row className="od-page-header__actions align-items-center g-3">
                <Col xs="auto">
                  <Button
                    variant="light-primary"
                    className="uw-btn uw-btn-lg btn-lg btn-active-light-primary"
                    disabled={loading}
                    onClick={() => {
                      window.history.back();
                      const validationArray = Array.from(
                        { length: initData.record.variants.length },
                        () => ({
                          stockCount: false,
                        })
                      );
                      setSelectedProductValidation(validationArray);
                      setSelectedProduct(initData.record.variants);
                      setDetails(initData);
                    }}
                  >
                    Cancel
                  </Button>
                </Col>
                <Col xs="auto">
                  <Button
                    variant="primary"
                    className="uw-btn uw-btn-primary uw-btn-lg btn-lg"
                    disabled={loading || !hasUnsavedChanges()}
                    onClick={() => {
                      if (isAddressOnlyMode) {
                        handleEditOrder();
                        return;
                      }
                      setShowSaveChangesModal(true);
                      setModificationReason('');
                      setModificationReasonError('');
                    }}
                  >
                    {!loading && (
                      <span className="indicator-label"> Save changes</span>
                    )}
                    {loading && (
                      <span
                        className="indicator-progress"
                        style={{ display: 'block' }}
                      >
                        Please wait...
                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                      </span>
                    )}
                  </Button>
                </Col>
              </Row>
            </div>
          </Col>
          {fetchLoading ? (
            <div className="d-flex justify-content-center">
                <Loader loading={fetchLoading} />
              </div>
          ) : (
            <>
              {selectedProduct &&
              orderValue &&
              Object?.keys(orderValue)?.length ? (
                <>
                  {changeDeliveryAddress ? (
                    <Suspense fallback={null}>
                      <ChangeDeliveryAddress
                        show={changeDeliveryAddress}
                        onHide={() => setChangeDeliveryAddress(false)}
                        onSaveAddress={handleSaveAddress}
                        details={details}
                        moduleName={OrdersDelivery}
                      />
                    </Suspense>
                  ) : (
                    <></>
                  )}
                  <Col md={6}>
                    <Card className="od-card">
                      <Card.Header>
                        <Card.Title>Order details</Card.Title>
                      </Card.Header>
                      <Card.Body>
                        <dl className="od-info-list">
                          <OrderInfoRow label="Customer name">
                            {details?.record?.customer?.name}
                          </OrderInfoRow>
                          <OrderInfoRow label="Order ID">
                            {details?.record?.refKey}
                          </OrderInfoRow>
                          <OrderInfoRow label="Order placed on">
                            {Method.convertDateToFormat(
                              details?._createdAt,
                              'DD/MM/YYYY [-] HH:mm'
                            )}
                          </OrderInfoRow>
                          <OrderInfoRow label="Payment method">
                            {details?.record?.payment?.paymentMode === OrderCash
                              ? 'Cash'
                              : ''}
                            {details?.record?.payment?.paymentMode ===
                            OrderTigoPesa
                              ? 'Tigo pesa'
                              : ''}
                            {details?.record?.payment?.paymentMode ===
                            OrderMPesa
                              ? 'MPesa'
                              : ''}
                            {details?.record?.payment?.paymentMode === OrderCard
                              ? 'Card'
                              : ''}
                          </OrderInfoRow>
                          <OrderInfoRow label="Order type">
                            {isPharmacyOrder ? 'Pharmacy' : 'Utrade'}
                          </OrderInfoRow>
                        </dl>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="od-card">
                      <Card.Header>
                        <Card.Title>Delivery address</Card.Title>
                        {allowAddressEdit ? (
                          <Button
                            variant="link"
                            className="btn-flush"
                            title="Change delivery address"
                            aria-label="Change delivery address"
                            onClick={() => setChangeDeliveryAddress(true)}
                          >
                            <img src={editIcon} alt="" />
                          </Button>
                        ) : (
                          <></>
                        )}
                      </Card.Header>
                      <Card.Body>
                        <dl className="od-info-list">
                          <OrderInfoRow label="Shop name">
                            {details?.record?.address?.name}
                          </OrderInfoRow>
                          <OrderInfoRow label="Phone number">
                            {details?.record?.address?.phoneCountry}{' '}
                            {details?.record?.address?.phone}
                          </OrderInfoRow>
                          <OrderInfoRow label="Delivery address">
                            {details?.record?.address?.addressLine1}
                            {', '}
                            {details?.record?.address?.landmark
                              ? details?.record?.address?.landmark
                              : ''}
                          </OrderInfoRow>
                          <OrderInfoRow label="District & city">
                            {details?.record?.address?.districtName}
                            {', '}
                            {details?.record?.address?.city}
                          </OrderInfoRow>
                        </dl>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={12}>
                    <Card className="od-card od-table-card">
                      <Card.Header>
                        <Card.Title>Products</Card.Title>
                        <span className="od-chip od-chip--muted">
                          {selectedProduct?.length}{' '}
                          {selectedProduct?.length === 1 ? 'item' : 'items'}
                        </span>
                      </Card.Header>
                      <Card.Body className="p-0">
                        <div className="table-responsive">
                          <table className="table table-row-bordered align-middle gs-9 gy-5 mb-0">
                            <thead>
                              <tr className="border-bottom align-middle">
                                <th className="w-md-375px min-w-200px">
                                  Product name
                                </th>
                                <th className="min-w-100px">Type</th>
                                <th className="min-w-125px">Unit price</th>
                                <th className="min-w-150px">Qty.</th>
                                <th className="w-md-100px">Tax(%)</th>
                                <th className="min-w-125px text-end">
                                  Total amount
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedProduct?.map(
                                (val: any, index: number) => {
                                  const validationKey = `${val?.quantityType}-${val?.variant._id}`;
                                  const productLoaded = isProductLoaded(val);
                                  const showQtyLimitHint = Boolean(
                                    validation[validationKey] &&
                                      (validation[validationKey]?.isInvalid ||
                                        val.stockCount >=
                                          (validation[validationKey]
                                            ?.maxLimit || 0))
                                  );
                                  return (
                                    <tr
                                      key={`${validationKey}-${index}`}
                                      className={clsx(
                                        productLoaded && 'od-row--loaded'
                                      )}
                                    >
                                      <td>
                                        <div className="od-product">
                                          <ImageWithFallback
                                            src={val?.variant?.media?.[0]?.url}
                                            alt={val?.variant?.title}
                                            className="od-product__thumb"
                                          />
                                          <div className="od-product__text">
                                            <span className="od-product__title">
                                              {val?.variant?.title?.replace(
                                                /\s*\)\s*/g,
                                                ')'
                                              )}
                                            </span>
                                            <span className="od-product__subtitle">
                                              {
                                                val?.variant?.product
                                                  ?.business?.name
                                              }
                                            </span>
                                            {productLoaded ? (
                                              <span className="od-chip od-chip--muted mt-2">
                                                Loaded, cannot be edited
                                              </span>
                                            ) : (
                                              <></>
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                      <td>
                                        <span className="fs-15 fw-500">
                                          {val?.quantityType ===
                                            CartonWithDozens ||
                                          val?.quantityType === CartonWithPieces
                                            ? 'Cartons'
                                            : ''}
                                          {val?.quantityType === Dozen
                                            ? 'Dozens'
                                            : ''}
                                          {val?.quantityType === Piece
                                            ? 'Pieces'
                                            : ''}
                                        </span>
                                      </td>
                                      <td>
                                        <span className="fs-15 fw-600">
                                          TSh{' '}
                                          {Method.getGeneralizedAmount(
                                            getEditOrderUnitPrice(val)
                                          )}
                                        </span>
                                        {getEditOrderUnitPrice(val) !==
                                        getEditOrderOriginalUnitPrice(val) ? (
                                          <span className="od-price-strike">
                                            TSh{' '}
                                            {Method.getGeneralizedAmount(
                                              getEditOrderOriginalUnitPrice(val)
                                            )}
                                          </span>
                                        ) : (
                                          <></>
                                        )}
                                      </td>
                                      <td>
                                        <div
                                          className={clsx(
                                            'od-stepper',
                                            (!allowProductEdit ||
                                              productLoaded) &&
                                              'od-stepper--disabled',
                                            showQtyLimitHint &&
                                              'od-stepper--limit'
                                          )}
                                        >
                                          <button
                                            type="button"
                                            className="od-stepper__btn"
                                            aria-label="Decrease quantity"
                                            onClick={() => {
                                              const currentStockCount =
                                                parseInt(
                                                  val.stockCount !== undefined
                                                    ? val.stockCount
                                                    : 0
                                                );
                                              if (currentStockCount > 0) {
                                                void handleQuantityChange(
                                                  currentStockCount - 1,
                                                  index,
                                                  val,
                                                  val.quantityType
                                                );
                                              }
                                            }}
                                            disabled={
                                              !allowProductEdit ||
                                              productLoaded ||
                                              val.stockCount <= 0
                                            }
                                          >
                                            &minus;
                                          </button>
                                          <input
                                            type="text"
                                            inputMode="numeric"
                                            aria-label="Quantity"
                                            value={
                                              val.stockCount !== undefined
                                                ? val.stockCount
                                                : 1
                                            }
                                            className="od-stepper__input"
                                            readOnly={
                                              !allowProductEdit || productLoaded
                                            }
                                            disabled={
                                              !allowProductEdit || productLoaded
                                            }
                                            onChange={(event: any) => {
                                              if (
                                                !allowProductEdit ||
                                                productLoaded
                                              ) {
                                                return;
                                              }
                                              const currentStockCount =
                                                parseInt(event.target.value);
                                              if (
                                                currentStockCount >= 0 ||
                                                isNaN(currentStockCount)
                                              )
                                                void handleQuantityChange(
                                                  currentStockCount,
                                                  index,
                                                  val,
                                                  val.quantityType
                                                );
                                            }}
                                            onKeyPress={(event: any) => {
                                              handleOnKeyPress(event);
                                            }}
                                          />
                                          <button
                                            type="button"
                                            className="od-stepper__btn"
                                            aria-label="Increase quantity"
                                            onClick={() => {
                                              const currentStockCount =
                                                Number.parseInt(
                                                  val.stockCount !== undefined
                                                    ? val.stockCount
                                                    : 0
                                                );
                                              if (currentStockCount >= 0) {
                                                void handleQuantityChange(
                                                  currentStockCount + 1,
                                                  index,
                                                  val,
                                                  val.quantityType
                                                );
                                              }
                                            }}
                                            disabled={
                                              !allowProductEdit ||
                                              productLoaded ||
                                              val.stockCount ===
                                                validation[validationKey]
                                                  ?.remainingQty
                                            }
                                          >
                                            +
                                          </button>
                                        </div>
                                        {showQtyLimitHint ? (
                                          <span className="od-qty-hint text-danger">
                                            {validation[validationKey]
                                              ?.limitType === 'order' ? (
                                              <>
                                                You can add Max{' '}
                                                {
                                                  validation[validationKey]
                                                    ?.maxLimit
                                                }{' '}
                                                Quantity for this order
                                              </>
                                            ) : (
                                              <>
                                                You can add up to{' '}
                                                {
                                                  validation[validationKey]
                                                    ?.maxLimit
                                                }{' '}
                                                products
                                              </>
                                            )}
                                          </span>
                                        ) : (
                                          <></>
                                        )}
                                      </td>
                                      <td>
                                        <span className="fs-15 fw-500">
                                          {val.productTax
                                            ? val.productTax + '%'
                                            : '0%'}
                                        </span>
                                      </td>
                                      <td className="text-end">
                                        <span className="fs-16 fw-bolder">
                                          TSh{' '}
                                          {Method.getGeneralizedAmount(
                                            getEditOrderTotalPrice(val)
                                          )}
                                        </span>
                                        {getEditOrderTotalPrice(val) !==
                                        getEditOrderOriginalTotalPrice(val) ? (
                                          <span className="od-price-strike">
                                            TSh{' '}
                                            {Method.getGeneralizedAmount(
                                              getEditOrderOriginalTotalPrice(
                                                val
                                              )
                                            )}
                                          </span>
                                        ) : (
                                          <></>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                }
                              )}
                            </tbody>
                          </table>
                        </div>
                        <div className="od-summary">
                          <div className="od-summary__list">
                            <div className="od-summary__row">
                              <span>Subtotal</span>
                              <span>
                                TSh{' '}
                                {Method.getGeneralizedAmount(
                                  orderValue.subTotal
                                )}
                              </span>
                            </div>
                            <div className="od-summary__row">
                              <span>U-Trade Discount</span>
                              {discount ? (
                                <div className="od-summary__discount">
                                  <InputGroup
                                    className={clsx(
                                      'min-w-50px border border-r8px',
                                      discountValidation ? 'border-danger' : ''
                                    )}
                                  >
                                    <InputGroup.Text className="border-0 bg-white px-3 pe-1 fs-15 fw-600">
                                      TSh
                                    </InputGroup.Text>
                                    <Form.Control
                                      type="number"
                                      className="form-control-custom min-h-45px bg-white border-0 ps-1"
                                      placeholder="0"
                                      disabled={!allowDiscountEdit}
                                      value={
                                        discountValue?.length
                                          ? discountValue[0]?.value
                                          : ''
                                      }
                                      onChange={(event: any) => {
                                        void handleDiscount(event.target.value);
                                      }}
                                    />
                                  </InputGroup>
                                  {allowDiscountEdit ? (
                                    <Button
                                      variant="link"
                                      className="btn-flush ms-3"
                                      title="Remove discount"
                                      aria-label="Remove discount"
                                      onClick={() => {
                                        setDiscountValue([]);
                                        setDiscount(false);
                                      }}
                                    >
                                      <img src={CrossIcon} alt="" />
                                    </Button>
                                  ) : (
                                    <></>
                                  )}
                                </div>
                              ) : (
                                <>
                                  {allowDiscountEdit ? (
                                    <Button
                                      variant="link"
                                      className="btn-flush text-primary fs-15 fw-600"
                                      onClick={() => {
                                        setDiscount(true);
                                      }}
                                    >
                                      + Add discount
                                    </Button>
                                  ) : (
                                    <span>
                                      TSh{' '}
                                      {Method.getGeneralizedAmount(
                                        discountValue?.[0]?.value || 0
                                      )}
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                            {orderValue?.discount > 0 ? (
                              <div className="od-summary__row">
                                <span>Product Discount</span>
                                <span className="text-success">
                                  - TSh{' '}
                                  {Method.getGeneralizedAmount(
                                    orderValue.discount
                                  )}
                                </span>
                              </div>
                            ) : (
                              <></>
                            )}
                            <div className="od-summary__row">
                              <span>Tax</span>
                              <span>
                                TSh{' '}
                                {Method.getGeneralizedAmount(
                                  orderValue.totalAmount -
                                    orderValue.totalAmountWithTax
                                )}
                              </span>
                            </div>
                            <div className="od-summary__row od-summary__row--total">
                              <span>Grand Total</span>
                              <span>
                                TSh{' '}
                                {discountValidation
                                  ? Method.getGeneralizedAmount(
                                      orderValue.totalAmount
                                    )
                                  : discountValue?.length
                                  ? Method.getGeneralizedAmount(
                                      parseInt(orderValue?.totalAmount) -
                                        parseInt(discountValue[0]?.value)
                                    )
                                  : Method.getGeneralizedAmount(
                                      orderValue.totalAmount
                                    )}
                              </span>
                            </div>
                            {orderValue.commission ? (
                              <div className="od-summary__row od-summary__row--note">
                                <span>U-Trade commission</span>
                                <span>
                                  TSh{' '}
                                  {Method.getGeneralizedAmount(
                                    orderValue.commission
                                  )}
                                </span>
                              </div>
                            ) : (
                              <></>
                            )}
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </>
              ) : (
                <></>
              )}
            </>
          )}
        </Row>
      </div>
  );
};
export default EditOrderDetails;
